import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { formatIQD, todayISO, formatDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Copy, Check, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import StatusBadge from "../components/StatusBadge";

export default function DailyOrders() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const showForm = params.get("new") === "1";

  const [orders, setOrders] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [restaurantId, setRestaurantId] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [meal, setMeal] = useState("");
  const [price, setPrice] = useState(5000);
  const [notes, setNotes] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/daily-orders").then((r) => setOrders(r.data));
    api.get("/restaurants").then((r) => setRestaurants(r.data.filter((x) => x.status === "Active")));
    api.get("/employees", { params: { status: "Active" } }).then((r) => setEmployees(r.data));
  };
  useEffect(() => { load(); }, []);

  const filtered = employees.filter(
    (e) => !query || e.name.toLowerCase().includes(query.toLowerCase()) || (e.phone || "").includes(query)
  );

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(filtered.map((e) => e.id)));
  const deselectAll = () => setSelected(new Set());

  const total = useMemo(() => Number(price || 0) * selected.size, [price, selected]);

  const copyPrevious = async () => {
    if (orders.length === 0) {
      toast.info("No previous orders to copy.");
      return;
    }
    const prev = orders[0];
    const detail = await api.get(`/daily-orders/${prev.id}`).then((r) => r.data);
    setDate(todayISO());
    setRestaurantId(detail.restaurant_id || "");
    setRestaurantName(detail.restaurant_name || "");
    setMeal(detail.meal_description || "");
    setPrice(detail.price_per_employee || 5000);
    setSelected(new Set(detail.employee_ids || []));
    toast.success(`Copied ${detail.employee_ids?.length || 0} employees from ${detail.order_date}`);
  };

  const submit = async () => {
    if (selected.size === 0) return toast.error("Select at least one employee");
    if (Number(price) <= 0) return toast.error("Enter a valid price");
    setSaving(true);
    try {
      await api.post("/daily-orders", {
        order_date: date,
        restaurant_id: restaurantId || null,
        restaurant_name: restaurantName || (restaurants.find((r) => r.id === restaurantId)?.name || ""),
        meal_description: meal,
        price_per_employee: Number(price),
        notes,
        employee_ids: Array.from(selected),
      });
      toast.success(`Created ${selected.size} food orders — Total ${formatIQD(total)}`);
      setSelected(new Set());
      setMeal(""); setNotes("");
      load();
      navigate("/orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="daily-orders-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-2xl font-extrabold tracking-tight">Daily Orders</h1>
          <p className="text-sm text-muted-foreground">Create today's order in seconds. Select employees, set the price, done.</p>
        </div>
        {!showForm && (
          <Button data-testid="btn-open-new-order" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate("/orders?new=1")}>
            <Plus size={16} className="mr-1" /> New Daily Order
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-5 space-y-5" data-testid="new-order-card">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="order-date" className="mt-1.5" />
            </div>
            <div>
              <Label>Restaurant</Label>
              <Select value={restaurantId || "none"} onValueChange={(v) => { setRestaurantId(v === "none" ? "" : v); }}>
                <SelectTrigger data-testid="order-restaurant" className="mt-1.5"><SelectValue placeholder="Select restaurant" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None (custom name) —</SelectItem>
                  {restaurants.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Custom restaurant name (optional)</Label>
              <Input value={restaurantName} onChange={(e) => setRestaurantName(e.target.value)} data-testid="order-restaurant-name" className="mt-1.5" placeholder="e.g. Downtown Kitchen" />
            </div>
            <div>
              <Label>Meal / Description</Label>
              <Input value={meal} onChange={(e) => setMeal(e.target.value)} data-testid="order-meal" className="mt-1.5" placeholder="Chicken + Rice" />
            </div>
            <div>
              <Label>Price per Employee (IQD)</Label>
              <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="order-price" className="mt-1.5" />
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {[3000, 5000, 7500, 10000].map((v) => (
                  <button key={v} type="button" className="text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700" onClick={() => setPrice(v)}>
                    {v.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-3">
              <Label>Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="order-notes" className="mt-1.5" />
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="heading font-bold">Select Employees</div>
              <div className="flex gap-2 items-center">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search employees" data-testid="order-emp-search" className="pl-8 h-9 w-56" />
                </div>
                <Button variant="outline" size="sm" onClick={selectAll} data-testid="btn-select-all">Select All</Button>
                <Button variant="outline" size="sm" onClick={deselectAll} data-testid="btn-deselect-all">Deselect All</Button>
                <Button variant="outline" size="sm" onClick={copyPrevious} data-testid="btn-copy-previous"><Copy size={14} className="mr-1" />Copy Previous</Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto p-1">
              {filtered.map((e) => {
                const on = selected.has(e.id);
                return (
                  <label
                    key={e.id}
                    data-testid={`emp-row-${e.id}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition ${on ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-border hover:bg-muted/50"}`}
                  >
                    <Checkbox checked={on} onCheckedChange={() => toggle(e.id)} data-testid={`emp-check-${e.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{e.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{e.phone || e.email || "—"}</div>
                    </div>
                    {on && <Check size={16} className="text-emerald-600" />}
                  </label>
                );
              })}
              {filtered.length === 0 && <div className="text-sm text-muted-foreground p-4">No active employees. Add some in the Employees page.</div>}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl bg-slate-900 text-white p-4 flex-wrap gap-3" data-testid="order-summary">
            <div className="flex gap-8 flex-wrap">
              <div>
                <div className="text-[11px] uppercase text-slate-400 tracking-wider">Selected</div>
                <div className="heading text-2xl font-extrabold">{selected.size}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-400 tracking-wider">Price / Employee</div>
                <div className="heading text-2xl font-extrabold">{formatIQD(price)}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-emerald-300 tracking-wider">Total Order</div>
                <div className="heading text-2xl font-extrabold text-emerald-300">{formatIQD(total)}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate("/orders")} className="bg-white/10 border-white/20 text-white hover:bg-white/20">Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="btn-create-orders" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                {saving ? "Creating…" : "Create Daily Orders"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Recent orders */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <div className="heading font-bold">Recent Daily Orders</div>
            <div className="text-xs text-muted-foreground">History of daily food orders</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-3">Date</th>
                <th className="p-3">Restaurant</th>
                <th className="p-3">Meal</th>
                <th className="p-3 text-right">Employees</th>
                <th className="p-3 text-right">Price</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t table-row-hover" data-testid={`order-list-${o.id}`}>
                  <td className="p-3 font-medium">{formatDate(o.order_date)}</td>
                  <td className="p-3">{o.restaurant_name || "—"}</td>
                  <td className="p-3">{o.meal_description || "—"}</td>
                  <td className="p-3 text-right mono">{o.total_employees}</td>
                  <td className="p-3 text-right mono">{formatIQD(o.price_per_employee)}</td>
                  <td className="p-3 text-right mono font-semibold">{formatIQD(o.total_amount)}</td>
                  <td className="p-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                      onClick={() => setConfirmDelete(o)}
                      data-testid={`btn-delete-daily-${o.id}`}
                    >
                      <Trash2 size={14} className="mr-1" />Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground">No daily orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-daily">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this daily order?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block font-medium text-slate-900 dark:text-slate-100 mb-2">
                {confirmDelete && `${formatDate(confirmDelete.order_date)} · ${confirmDelete.restaurant_name || "—"} · ${confirmDelete.total_employees} employees · ${formatIQD(confirmDelete.total_amount)}`}
              </span>
              This permanently removes the daily order and <strong>every individual food order</strong> created from it. Any payments that were allocated to those orders will be automatically re-allocated to each employee's remaining unpaid orders (FIFO). Restaurant payments tied to this daily order are also removed. An audit-log entry is recorded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  const { data } = await api.delete(`/daily-orders/${confirmDelete.id}`);
                  toast.success(`Daily order deleted · ${data.orders_removed} employee orders removed${data.payments_reallocated ? ` · ${data.payments_reallocated} payment(s) re-allocated` : ""}`);
                  setConfirmDelete(null);
                  load();
                } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
              }}
              data-testid="btn-confirm-delete-daily"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
